"use client";

import { motion } from "framer-motion";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

const services = [
  {
    icon: "MagnifyingGlassIcon",
    title: "Product Sourcing",
    tagline: "Direct factory access, zero guesswork",
    description:
      "European-operated team with deep Guangdong factory relationships. We handle supplier discovery, price negotiation, sample coordination, and quality assurance — so you get factory-direct pricing without the communication chaos.",
    features: [
      "Supplier vetting & background checks",
      "Price negotiation & sample coordination",
      "Quality control inspections (pre-shipment)",
      "No sourcing fee for fulfillment clients",
      "Access to 500+ verified factories",
    ],
  },
  {
    icon: "BuildingStorefrontIcon",
    title: "Warehousing",
    tagline: "Secure storage, real-time visibility",
    description:
      "Our Shenzhen warehouse is strategically located 20 minutes from major manufacturing hubs. Climate-controlled, security-monitored, and connected to our tech-driven inventory management platform.",
    features: [
      "Real-time inventory tracking dashboard",
      "Low-stock alerts via email & Slack",
      "Climate-controlled storage",
      "Barcode & SKU management",
      "Flexible monthly storage billing",
    ],
  },
  {
    icon: "ArchiveBoxIcon",
    title: "Pick & Pack",
    tagline: "Accurate. Fast. Under 0.3% error rate.",
    description:
      "Every order is picked, verified, and packed with care. We operate a three-check quality system to ensure the right product, right quantity, and right packaging reaches your customer.",
    features: [
      "Triple-check verification system",
      "Under 0.3% order error rate",
      "Same-day dispatch for orders before 2PM CST",
      "Kitting & bundling support",
      "Fragile item handling protocols",
    ],
  },
  {
    icon: "TruckIcon",
    title: "Worldwide Shipping",
    tagline: "US & EU in 5–8 days. Tracked, guaranteed.",
    description:
      "We've negotiated exclusive rates with DHL, FedEx, Yanwen, and regional carriers. Every shipment is tracked end-to-end with automatic customer notifications at each milestone.",
    features: [
      "🇺🇸 US: 5–8 business days",
      "🇬🇧 UK: 4–7 business days",
      "🇪🇺 EU: 5–8 business days",
      "🇦🇺 AU: 6–9 business days",
      "Express options available (2–4 days)",
    ],
  },
  {
    icon: "GiftIcon",
    title: "Custom Packaging & Private Label",
    tagline: "Your brand. Not a brown box.",
    description:
      "We design and produce custom branded packaging, inserts, and labels. Your customers receive a premium unboxing experience that reflects your brand — not a generic dropship package.",
    features: [
      "Custom printed boxes & mailers",
      "Thank-you cards & brand inserts",
      "Private label & product customization",
      "Low MOQ on packaging (100 units)",
      "Design support available",
    ],
  },
  {
    icon: "ArrowPathIcon",
    title: "Returns Management",
    tagline: "Hassle-free returns processing",
    description:
      "We handle the entire returns process — from customer communication to quality inspection and restocking. You get full reporting on return reasons to help improve your product.",
    features: [
      "Returns portal for customers",
      "Quality inspection on all returns",
      "Restocking or disposal options",
      "Monthly return analytics reports",
      "Refund coordination with your store",
    ],
  },
];

export default function ServicesGrid() {
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.12
      }
    },
    viewport: { once: true, margin: "-60px" }
  };

  const cardVariant = {
    initial: { opacity: 0, y: 50, scale: 0.95 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-secondary-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: "-60px" }}
        >
          {services?.map((service, index) => (
            <motion.div
              key={service?.title}
              className="glass-card rounded-3xl sm:rounded-4xl p-6 sm:p-8 lg:p-10 border border-glass-border backdrop-blur-xl transition-all duration-500 hover:shadow-glass-hover hover:-translate-y-1"
              variants={cardVariant}
            >
              <div className="flex items-start gap-4 sm:gap-5 mb-5 sm:mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center shrink-0">
                  <Icon name={service?.icon} size={24} className="text-accent-primary" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-extrabold font-heading text-text-primary mb-1 sm:mb-2">
                    {service?.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-accent-secondary font-semibold">
                    {service?.tagline}
                  </p>
                </div>
              </div>
              <p className="text-sm sm:text-base text-text-secondary leading-relaxed mb-5 sm:mb-6">
                {service?.description}
              </p>
              <ul className="space-y-2.5 sm:space-y-3">
                {service?.features?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2.5 sm:gap-3 text-xs sm:text-sm text-text-secondary">
                    <Icon name="CheckCircleIcon" size={18} className="text-accent-primary shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section - Mobile optimized */}
        <motion.div
          className="mt-12 sm:mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Link
            href="/contact"
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl bg-accent-primary text-white font-bold text-sm sm:text-base transition-all duration-300 hover:scale-105 min-h-[52px] sm:min-h-[56px] w-full sm:w-auto"
            style={{
              boxShadow: "0 0 40px rgba(91, 46, 255, 0.4)"
            }}
          >
            <Icon name="PhoneIcon" size={18} />
            Book a Strategy Call
          </Link>
        </motion.div>
      </div>
    </section>
  );
}